import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { translate as translateBase } from 'loankeeper-common/helpers'
import {
  LoanSearchParams,
  LoanSearchServerData,
  LoanViewParams,
  LoanViewServerData,
  UpdateCommentsParams,
  AddNoteParams,
  EditNoteParams,
  DeleteNoteParams,
  AddEditMonitoringParams,
  DeleteMonitoringParams,
  UpdateCustomFieldsParams,
  LoanCoverParams,
  LoanCoverServerData,
  LoanAddParams,
  LoanAddServerData,
  LoanScheduleParams,
  LoanScheduleServerData,
  LoanRestRServerData,
  LoanRestRShortParams,
  LoanOperParams,
  LoanOperServerData,
  LoanOperPages,
  UpdateResourceIDParams,
  StopAccuralsParams,
  UndoStopAccuralsParams,
  AddConditionParams,
  DeleteConditionParams,
  AddOfficerAssistantParams,
  DeleteOfficerParams,
  DeleteAssistantParams,
  DeleteTransactionParams,
  WriteOffParams,
  WriteOffDataParams,
  AddPermissionParams,
  DeletePermissionParams,
  ReserveManualParams,
  ReserveAutoParams,
  LinkCollateralParams,
  UnlinkCollateralParams,
  CloseRevolvingParams,
  DeleteTagParams,
  LoanVisaUndoParams,
  WriteOffUndoParams,
  DisburseValidateParams,
  LoanDeleteParams,
  DisburseUltParams,
  LoanVisaParams,
  BlockLoanParams,
  UndoBlockLoanParams,
  UpdateAgreementNumberParams,
  PrintLoanParams,
  LoanViewPages,
  PrintLoanAgreementParams,
  UpdateNodeIDParams,
  AddTagParams,
} from '@lk/api'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { ThunkApi } from '../index.d'

const translate = (key: string) => translateBase(`LKMESSAGE-${key}`)

export const loanSearch = createAsyncThunk<any, LoanSearchParams & { instanceName?: string }, ThunkApi>(
  '[LOAN] SEARCH',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const { instanceName } = params

    if (instanceName) {
      delete params.instanceName
    }

    const result = await extra.api.loan.loanSearch(params)

    if (result.success) {
      if (instanceName) {
        return {
          instanceName: instanceName,
          data: result.data,
        }
      }
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to search loan')
  },
)

export const loanSearchAgain = createAsyncThunk<LoanSearchServerData, LoanSearchParams | void, ThunkApi>(
  '[LOAN] SEARCH_MAIN',
  async (_params, { extra, rejectWithValue, getState }) => {
    const prevParams = getState().loan.loanData.prevSearchParams

    const result = await extra.api.loan.loanSearch(prevParams)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const destroyLoans = createAction('[LOAN] DELETE_LOANS')

export const loanView = createAsyncThunk<LoanViewServerData, LoanViewParams, ThunkApi>(
  '[LOAN] VIEW',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanView(params)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to view loan')
  },
)

export const accountsDisburse = createAsyncThunk<LoanViewServerData, LoanViewParams, ThunkApi>(
  '[LOAN] ACCOUNTS_DISBURSE',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanView(params)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to view loan')
  },
)

export const destroyAccountsDisburse = createAction('[LOAN] DESTROY_ACCOUNTS_DISBURSE')

export const fetchConditionInitialFields = createAsyncThunk<
  LoanViewServerData,
  { LoanID: number | string; HashSum_LoanID_: string },
  ThunkApi
>('[LOAN] FETCH_CONDITION_INITIAL_FIELDS', async (params, { extra, rejectWithValue }) => {
  const result = await extra.api.loan.loanView({
    ...params,
    page: 'conditions_curr',
  })

  if (result.success) {
    return result.data
  }

  return rejectWithValue(result.error.message)
})

export const printLoan = createAsyncThunk<LoanViewServerData, Omit<PrintLoanParams, 'page'>, ThunkApi>(
  '[LOAN] VIEW',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanView({
      ...params,
      page: LoanViewPages.PRINTLOANCOLDETAGREE,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loanPrint = createAsyncThunk<LoanViewServerData, Omit<PrintLoanParams, 'page'>, ThunkApi>(
  '[LOAN] VIEW',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanView({
      ...params,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const printLoanAgreement = createAsyncThunk<
  LoanViewServerData,
  Omit<PrintLoanAgreementParams, 'page'>,
  ThunkApi
>('[LOAN] VIEW', async (params, { extra, rejectWithValue }) => {
  const result = await extra.api.loan.loanView({
    ...params,
    page: LoanViewPages.PRINTLOANAGREEMENT,
  })

  if (result.success) {
    return result.data
  }

  return rejectWithValue(result.error.message)
})

export const updateResourceID = createAsyncThunk<LoanOperServerData, Omit<UpdateResourceIDParams, 'page'>, ThunkApi>(
  '[LOAN] UPDATE_RESOURCE_ID',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.RESOURCEUPDATE,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const stopAccruals = createAsyncThunk<LoanOperServerData, Omit<UndoStopAccuralsParams, 'page'>, ThunkApi>(
  '[LOAN] STOP_ACCURALS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.STOPACCURALS,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const undoStopAccruals = createAsyncThunk<LoanOperServerData, Omit<StopAccuralsParams, 'page'>, ThunkApi>(
  '[LOAN] UNDO_STOP_ACCURALS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.UNDOSTOPACCURALS,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addCondition = createAsyncThunk<LoanOperServerData, Omit<AddConditionParams, 'page'>, ThunkApi>(
  '[LOAN] ADD_CONDITION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.ADDCONDITION,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteCondition = createAsyncThunk<LoanOperServerData, Omit<DeleteConditionParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_CONDITION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DELETECONDITION,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addOfficerAssistant = createAsyncThunk<LoanOperServerData, AddOfficerAssistantParams, ThunkApi>(
  '[LOAN] ADD_OFFICER_ASSISTANT',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteOfficer = createAsyncThunk<LoanOperServerData, Omit<DeleteOfficerParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_OFFICER',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.OFFICERDELETE,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteAssistant = createAsyncThunk<LoanOperServerData, Omit<DeleteAssistantParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_ASSISTANT',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DELETEASSISTANT,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteTransaction = createAsyncThunk<LoanOperServerData, Omit<DeleteTransactionParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_TRANSACTION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DELETEATRNASACTION,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const writeOff = createAsyncThunk<LoanOperServerData, Omit<WriteOffParams, 'page'>, ThunkApi>(
  '[LOAN] WRITE_OFF',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.WRITEOFF,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const undoWriteOff = createAsyncThunk<LoanOperServerData, Omit<WriteOffUndoParams, 'page'>, ThunkApi>(
  '[LOAN] UNDO_WRITE_OFF',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.UNDOWRITEOFF,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const getwriteOffData = createAsyncThunk<unknown, WriteOffDataParams, ThunkApi>(
  '[LOAN] GET_WRITE_OFF_DATA',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanView({
      ...params,
      page: 'writeoffdata',
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addPermission = createAsyncThunk<unknown, Omit<AddPermissionParams, 'page'>, ThunkApi>(
  '[LOAN] ADD_PERMISSION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.ACCESSPERMADD,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deletePermission = createAsyncThunk<unknown, Omit<DeletePermissionParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_PERMISSION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DELETEPERMISSION,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const reserveManual = createAsyncThunk<unknown, Omit<ReserveManualParams, 'page'>, ThunkApi>(
  '[LOAN] RESERVE_MANUAL',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.RESERVEMANUAL,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const reserveAuto = createAsyncThunk<unknown, Omit<ReserveAutoParams, 'page'>, ThunkApi>(
  '[LOAN] RESERVE_AUTO',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.RESERVEAUTO,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const linkCollateral = createAsyncThunk<unknown, Omit<LinkCollateralParams, 'page'>, ThunkApi>(
  '[LOAN] LINK_COLLATERAL',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.LINKCOLLATERAL,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const unLinkCollateral = createAsyncThunk<unknown, Omit<UnlinkCollateralParams, 'page'>, ThunkApi>(
  '[LOAN] UNLINK_COLLATERAL',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.UNLINKCOLLATERAL,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const closeRevolving = createAsyncThunk<unknown, Omit<CloseRevolvingParams, 'page'>, ThunkApi>(
  '[LOAN] CLOSE_REVOLVING',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.CLOSEREVOLVING,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)
export const addTag = createAsyncThunk<unknown, Omit<AddTagParams, 'page'>, ThunkApi>(
  '[LOAN] ADD_TAG',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.ADDTAG,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteTag = createAsyncThunk<unknown, Omit<DeleteTagParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_TAG',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DELETETAG,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loanVisaUndo = createAsyncThunk<unknown, Omit<LoanVisaUndoParams, 'page'>, ThunkApi>(
  '[LOAN] UNDO_VISA',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.UNDOVISA,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const disburseValidate = createAsyncThunk<any, Omit<DisburseValidateParams, 'page'>, ThunkApi>(
  '[LOAN] DISBURSE_VALIDATE',
  async (params, { extra, rejectWithValue }) => {
    const saveData = params.saveData
    if (params.saveData) delete params.saveData

    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DISBURSEVALIDATE,
    })

    if (result.success) {
      return {
        ...result.data,
        saveData,
      }
    }

    return rejectWithValue(result.error.message)
  },
)

export const destroyDisburseValidate = createAction('[LOAN] DELETE_LOAN')

export const deleteLoan = createAsyncThunk<any, Omit<LoanDeleteParams, 'page'>, ThunkApi>(
  '[LOAN] DELETE_LOAN',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DELETE,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const disburseUlt = createAsyncThunk<any, Omit<DisburseUltParams, 'page'>, ThunkApi>(
  '[LOAN] DDISBURSE_ULT',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.DISBURSEULT,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loanVisa = createAsyncThunk<unknown, Omit<LoanVisaParams, 'page'>, ThunkApi>(
  '[LOAN] LOAN_VISA',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.VISA,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loanBlock = createAsyncThunk<unknown, Omit<BlockLoanParams, 'page'>, ThunkApi>(
  '[LOAN] BLOCK_LOAN',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.ARREST,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const undoLoanBlock = createAsyncThunk<unknown, Omit<UndoBlockLoanParams, 'page'>, ThunkApi>(
  '[LOAN] UNDO_BLOCK_LOAN',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.UNDOARREST,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const updateAgreementNumber = createAsyncThunk<unknown, Omit<UpdateAgreementNumberParams, 'page'>, ThunkApi>(
  '[LOAN] UPDATE_AGREEMENT_NUMBER',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.AGREEMENTNUMBERUPDATE,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const updateNodeID = createAsyncThunk<unknown, Omit<UpdateNodeIDParams, 'page'>, ThunkApi>(
  '[LOAN] UPDATE_NODE_ID',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanOper({
      ...params,
      page: LoanOperPages.NODEUPDATE,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loanRestRShort = createAsyncThunk<LoanRestRServerData, LoanRestRShortParams, ThunkApi>(
  '[LOAN] UPDATE_COMMENTS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanRestRShort(params)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to loanrestrshort')
  },
)

export const updateComments = createAsyncThunk<void, UpdateCommentsParams, ThunkApi>(
  '[LOAN] UPDATE_COMMENTS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.updateComments(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to update loan comment')
  },
)

export const addNote = createAsyncThunk<void, AddNoteParams, ThunkApi>(
  '[LOAN] ADD_NOTE',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.addNote(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to add loan note')
  },
)

export const editNote = createAsyncThunk<void, EditNoteParams, ThunkApi>(
  '[LOAN] EDIT_NOTE',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.editNote(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to edit loan note')
  },
)

export const deleteNote = createAsyncThunk<void, DeleteNoteParams, ThunkApi>(
  '[LOAN] DELETE_NOTE',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.deleteNote(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to delete loan note')
  },
)

export const addEditMonitoring = createAsyncThunk<void, AddEditMonitoringParams, ThunkApi>(
  '[LOAN] ADD_EDIT_MONITORING',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.addEditMonitoring(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to add/edit loan monitoring')
  },
)

export const deleteMonitoring = createAsyncThunk<void, DeleteMonitoringParams, ThunkApi>(
  '[LOAN] DELETE_MONITORING',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.deleteMonitoring(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to delete loan monitoring')
  },
)

export const updateCustomFields = createAsyncThunk<void, UpdateCustomFieldsParams, ThunkApi>(
  '[LOAN] UPDATE_CUSTOM_FIELDS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.updateCustomFields(params)

    if (result.success) {
      return
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to update loan custom fields')
  },
)

export const loanCover = createAsyncThunk<LoanCoverServerData, Partial<LoanCoverParams>, ThunkApi>(
  '[LOAN] LOAN_COVER',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanCover(params as LoanCoverParams)

    if (result.success) {
      return result.data
    }
    return rejectWithValue(result.error.message)
  },
)

export const loanAdd = createAsyncThunk<LoanAddServerData, LoanAddParams, ThunkApi>(
  '[LOAN] LOAN_ADD',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanAdd(params)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to add loan')
  },
)

export const loanSchedule = createAsyncThunk<LoanScheduleServerData, LoanScheduleParams, ThunkApi>(
  '[LOAN] LOAN_SCHEDULE',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.loan.loanSchedule(params)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to schedule loan')
  },
)
